<template>
  <div class="form-box">
    <div class="content">
      <header class="header">
        <span class="header-text">
          <template v-if="toCreate">New Item</template>
          <template v-else>Edit Item</template>
        </span>
        <simple-dropdown class="more-button">
          <template v-slot:dropdownMenu>
            <ul>
              <li>
                <button @click="copyFormValues">
                  Copy Current Values
                </button>
              </li>
              <li>
                <button @click="overwriteFormValues">
                  Paste to this Form
                </button>
              </li>
            </ul>
          </template>
        </simple-dropdown>
      </header>
      <div class="form">
        <div class="properties-form dense">
          <div class="property-item more-dropdown-button-container" v-for="(field,index) in fields"
               :key="index"
               :class="{'is-compulsory': field.is_compulsory, 'is-empty': isFieldEmpty(field)}"
          >
            <div class="property-label" :class="{'has-more-dropdown-button': field.data_type==='entity'}">
              <span class="label">{{ toTitleCase(field['field_name']) }}</span>
              <create-entity-dropdown class="more-dropdown-button"
                                      @copyEntities="copyEntitiesFrom(field.field_id)"
                                      @pasteEntities="pasteEntitiesTo(field.field_id)"
                                      v-if="field.data_type==='entity'"></create-entity-dropdown>
            </div>
            <div class="property-control">
              <component :is="getInputControlForField(field).component"
                         :initial-value="getInitialValueForFieldId(field['field_id'])"
                         v-bind="getInputControlForField(field).props"
                         :property-in-form="field['field_id']"
                         @valueChanged="valueChanged"
                         ref="inputControls"
              ></component>
            </div>
            <div class="shortcuts">
              <button
                  v-if="field.data_type === 'text' && (field.is_note_field || field.is_compulsory) && fieldIdsEmptyMaps[field.field_id]"
                  @click="setInputValue(field.field_id, 'Not Mentioned')">Not Mentioned
              </button>
              <button class="mt-1" v-if="canLoadFormulaToField(field)"
                      @click="updateFieldByFormula(field)">Load
                Formula
              </button>
            </div>
          </div>
        </div>
      </div>
      <footer class="footer">
        <button class="button-fancy mr-3" @click="onCancel">Cancel</button>
        <button class="button-fancy button-fancy-dark" v-if="toCreate" @click="onSubmit">Create</button>
        <button class="button-fancy button-fancy-dark" v-else @click="onSubmit">Save</button>
      </footer>
    </div>
  </div>
</template>

<script>
import TextField from "@/InputControls/TextField";
import {
  evaluateFieldValueFilled,
  evaluateValueFilled,
  toTitleCase,
  validateFields,
  formCopyHelper
} from "@/utils";
import CreateEntityDropdown from "@/components/CreateEntityDropdown";
import {getInputControlForField} from "@/InputControls/InputControlTools";
import FormulaControlBase from "@/InputControls/FormulaControlBase";
import EntitiesCopyBase from "@/InputControls/EntitiesCopyBase";
import SimpleDropdown from "@/components/SimpleDropdown";

export default {
  name: "FormBox",
  components: {SimpleDropdown, CreateEntityDropdown, TextField},
  props: {
    initialValues: Object,
    fields: Array,
    parentValues: Object,
    parentFieldId: {},
  },
  computed: {
    toCreate() {
      return !this.initialValues;
    },
    fieldIdToSchema() {
      const fieldIdToSchema = {};
      this.fields.forEach(item => {
        fieldIdToSchema[item.field_id] = item;
      });
      return fieldIdToSchema;
    },
    projectId() {
      return this.$route.params.pid;
    }
  },
  data() {
    return {
      initialized: false,
      fieldIdsEmptyMaps: {},
      fieldIdToValueMaps: {},
    }
  },
  methods: {
    toTitleCase,
    getInputControlForField,
    setInputValue(fieldId, value, focus) {
      if (typeof focus === 'undefined') {
        focus = true;
      }
      const controls = this.$refs.inputControls;
      for (let i = 0; i < controls.length; i++) {
        if (controls[i].propertyInForm === fieldId) {
          focus && controls[i].focus();
          controls[i].setInputValue(value);
          break
        }
      }
    },
    updateFieldIdsEmptyMapsForField(fieldId) {
      let control = this.getInputControlComponentByFieldId(fieldId);
      let field = this.getFieldTemplateByFieldId(fieldId);
      let isEmpty = !evaluateFieldValueFilled(control.getValue(), field);
      let comp;

      if (isEmpty) {
        if (typeof field.note !== "undefined" && field.note !== null) {
          comp = this.getInputControlComponentByFieldId(field.note);
          if (!comp) {
            // eslint-disable-next-line no-console
            console.error('the note field is null:', field, field.note)
          }
          isEmpty = !evaluateValueFilled(comp.getValue());
        }
      }

      this.fieldIdsEmptyMaps[fieldId] = isEmpty;
    },
    valueChanged(newVal, oldVal, fieldId) {
      if (!this.initialized) {
        return;
      }

      this.fieldIdToValueMaps[fieldId] = newVal;
      this.updateFieldIdsEmptyMapsForField(fieldId);

      let field = this.getNoteForFieldTemplate(fieldId);

      if (typeof field !== "undefined") {
        this.updateFieldIdsEmptyMapsForField(field.field_id);
      }
    },
    getInitialValueForFieldId(fieldId) {
      const initialValues = this.initialValues || {};
      return fieldId in initialValues ? initialValues[fieldId] : null;
    },
    getFieldTemplateByFieldId(fieldId) {
      return this.fieldIdToSchema[fieldId];
    },
    getInputControlComponentByFieldId(fieldId) {
      return this.$refs.inputControls && this.$refs.inputControls.filter(item => (item.propertyInForm === fieldId))[0] || null
    },
    getNoteForFieldTemplate(noteFieldId) {
      return this.fields.filter(item => (item.note === noteFieldId))[0];
    },
    initializeControls() {
      let map = {};
      let map2 = {};
      let isEmpty;
      let field, comp, value;
      this.$refs.inputControls.forEach(control => {
        value = control.getValue();
        field = this.getFieldTemplateByFieldId(control.propertyInForm);
        isEmpty = !evaluateFieldValueFilled(value, field);
        if (isEmpty) {
          if (typeof field.note !== "undefined" && field.note !== null) {
            if ((comp = this.getInputControlComponentByFieldId(field.note))) {
              isEmpty = !evaluateValueFilled(comp.getValue());
            }
          }
        }
        map[control.propertyInForm] = isEmpty;
        map2[control.propertyInForm] = value;
      });
      this.fieldIdsEmptyMaps = map;
      this.fieldIdToValueMaps = map2;

      this.initialized = true;
      this.$refs.inputControls[0].focus();
    },
    isFieldEmpty(field) {
      return this.fieldIdsEmptyMaps[field.field_id]
    },
    onSubmit() {
      const errors = validateFields(this.fields, undefined, this.fieldIdToValueMaps, true, true);
      if (errors.length) {
        let message = '';
        errors.forEach(item1 => {
          message += `\n` +
              ('fieldIndex' in item1 ? (`In property "${item1.fieldIndex + 1}. ${item1.fieldName}", `) : '') +
              `${item1.explanation}.`;
        });
        alert('The content did not pass validation. Here are more details.\n' + message);
        return;
      }
      this.$emit('submit', this.fieldIdToValueMaps);
    },
    onCancel() {
      this.$emit('cancel');
    },
    copyFormValues() {
      formCopyHelper.setCopyObj({
        projectId: this.projectId,
        parentFieldId: this.parentFieldId,
        formData: JSON.parse(JSON.stringify(this.fieldIdToValueMaps))
      })
    },
    overwriteFormValues() {
      const obj = formCopyHelper.getCopyObj();
      if (!obj) {
        alert('It seems you haven\'t copy any form! Nothing pasted.')
        return
      }
      const {projectId, parentFieldId, formData} = obj;
      if (projectId !== this.projectId) {
        alert('The copied source is from another project! Nothing pasted.')
        return;
      }
      if (parentFieldId !== this.parentFieldId) {
        alert('The copied source is from another attribute! Nothing pasted.')
        return;
      }
      this.fields.forEach(({field_id}) => {
        if (field_id in formData) {
          this.setInputValue(field_id, formData[field_id], false);
        }
      })
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initializeControls();
    })
  },
  mixins: [FormulaControlBase, EntitiesCopyBase]
}
</script>

<style scoped lang="scss">
@import "../assets/variables";

.form-box {

  .content {
    height: 100%;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    border: 1px solid lightgray;
    background-color: white;
    min-height: 320px;
    box-shadow: 0 2px 0 2px rgba(gray, 0.3);

    .form {
      flex: 1 auto;
      overflow: auto;
      padding: 0.75rem 0.75rem;
    }

    .header, .footer {
      text-align: center;

    }

    .header {
      padding: 0.25rem 0.75rem;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;

      .header-text {
        font-weight: 500;
      }

      .more-button {
        margin-left: 0.5rem;
      }
    }

    .footer {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0.5rem 0.75rem;
      margin-top: 0.5rem;
      border-top: 1px solid #e0e0e0;

      .cancel-button {
        margin-right: 0.75rem;
      }
    }
  }
}

.property-item {
  &.is-empty.is-compulsory {
    & > .property-label {
      & > .label {
        color: $warning-red;
      }
    }
  }
}
</style>
